import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Shield, Save, RotateCcw, Loader2, ChevronDown, Check, X } from 'lucide-react';
import { roleAccessSettingApi, RoleAccessSettings as RoleAccessSettingsType, Module, Scope } from '@/services/roleAccessSettingApi';
import SimpleSelect from '@/components/common/SimpleSelect';

const MODULE_LABELS: Record<Module, string> = {
  services: 'Services',
  tasks: 'Tasks',
  invoices: 'Invoices',
};

const SCOPE_LABELS: Record<Scope, { label: string; description: string }> = {
  ALL: { label: 'All', description: 'View all records in branch' },
  ASSIGNED: { label: 'Assigned', description: 'View only assigned records' },
  CREATED: { label: 'Created', description: 'View only created records' },
};

interface MultiSelectDropdownProps {
  label: string;
  selected: Scope[];
  options: Scope[];
  onChange: (selected: Scope[]) => void;
  disabled?: boolean;
}

function MultiSelectDropdown({ label, selected, options, onChange, disabled }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (scope: Scope) => {
    if (disabled) return;

    // If selecting ALL, clear others
    if (scope === 'ALL') {
      onChange(['ALL']);
      return;
    }

    // If already has ALL, remove it and add the new scope
    if (selected.includes('ALL')) {
      onChange([scope]);
      return;
    }

    // Toggle the scope
    if (selected.includes(scope)) {
      // Don't allow empty selection
      if (selected.length === 1) return;
      onChange(selected.filter((s) => s !== scope));
    } else {
      onChange([...selected, scope]);
    }
  };

  const getDisplayText = () => {
    if (selected.includes('ALL')) return 'All';
    if (selected.length === 0) return 'Select...';
    if (selected.length === 1) return SCOPE_LABELS[selected[0]].label;
    return selected.map((s) => SCOPE_LABELS[s].label).join(' & ');
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between px-3 py-2 text-sm border rounded-lg
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'}
          ${isOpen ? 'border-purple-500 ring-2 ring-purple-100' : 'border-gray-300'}
        `}
      >
        <span className={selected.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
          {getDisplayText()}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {options.map((scope) => {
              const isSelected = selected.includes(scope);
              const scopeInfo = SCOPE_LABELS[scope];

              return (
                <button
                  key={scope}
                  type="button"
                  onClick={() => handleToggle(scope)}
                  className={`
                    w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-gray-50
                    ${isSelected ? 'bg-purple-50' : ''}
                  `}
                >
                  <div className={`
                    mt-0.5 w-4 h-4 rounded border flex items-center justify-center
                    ${isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}
                  `}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{scopeInfo.label}</div>
                    <div className="text-xs text-gray-500">{scopeInfo.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function RoleAccessSettings() {
  const queryClient = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [localSettings, setLocalSettings] = useState<Record<Module, Scope[]> | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch all role access settings
  const { data, isLoading, error } = useQuery({
    queryKey: ['role-access-settings'],
    queryFn: roleAccessSettingApi.getAll,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ roleId, settings }: { roleId: string; settings: Record<Module, Scope[]> }) =>
      roleAccessSettingApi.updateByRole(roleId, settings),
    onSuccess: () => {
      toast.success('Access settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['role-access-settings'] });
      setHasChanges(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    },
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: (roleId: string) => roleAccessSettingApi.resetToDefault(roleId),
    onSuccess: (data) => {
      toast.success('Settings reset to defaults');
      queryClient.invalidateQueries({ queryKey: ['role-access-settings'] });
      setLocalSettings(data.settings);
      setHasChanges(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reset settings');
    },
  });

  // Set initial selected role
  useEffect(() => {
    if (data?.settings && data.settings.length > 0 && !selectedRoleId) {
      setSelectedRoleId(data.settings[0].roleId);
    }
  }, [data, selectedRoleId]);

  // Load settings when role changes
  useEffect(() => {
    if (data?.settings && selectedRoleId) {
      const roleSettings = data.settings.find((r) => r.roleId === selectedRoleId);
      if (roleSettings) {
        setLocalSettings(roleSettings.settings);
        setHasChanges(false);
      }
    }
  }, [data, selectedRoleId]);

  const handleScopeChange = (module: Module, scopes: Scope[]) => {
    if (!localSettings) return;
    setLocalSettings({ ...localSettings, [module]: scopes });
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!localSettings || !selectedRoleId) return;
    updateMutation.mutate({ roleId: selectedRoleId, settings: localSettings });
  };

  const handleReset = () => {
    if (!selectedRoleId) return;
    resetMutation.mutate(selectedRoleId);
  };

  const selectedRole = data?.settings.find((r) => r.roleId === selectedRoleId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600">Loading settings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        Failed to load role access settings. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Role Access Settings</h2>
            <p className="text-sm text-gray-500">Configure what data each role can view</p>
          </div>
        </div>
      </div>

      {/* Role Selector */}
      <div className="bg-gray-50 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Role</label>
        <SimpleSelect
          value={selectedRoleId}
          onChange={setSelectedRoleId}
          options={(data?.settings ?? []).map((role) => ({
            value: role.roleId,
            label: `${role.roleName}${role.isSystemRole ? ' (System)' : ''}`,
          }))}
          aria-label="Role"
          wrapperClassName="w-full md:w-64"
          className="focus:!ring-purple-500 focus:!border-purple-500"
        />
      </div>

      {/* Settings Grid */}
      {localSettings && selectedRole && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">
              Data Visibility for: <span className="text-purple-600">{selectedRole.roleName}</span>
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Default: All users see their branch data. Customize which records each role can view.
            </p>
          </div>

          <div className="p-4 space-y-4">
            {(Object.keys(MODULE_LABELS) as Module[]).map((module) => {
              // Get module-specific scopes from API response
              const moduleScopes = data?.scopesByModule?.[module] || ['ALL', 'ASSIGNED', 'CREATED'];

              return (
                <div key={module} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b border-gray-100 last:border-0">
                  <div className="sm:w-32 flex-shrink-0">
                    <label className="text-sm font-medium text-gray-700">
                      {MODULE_LABELS[module]}
                    </label>
                  </div>
                  <div className="flex-1 max-w-xs">
                    <MultiSelectDropdown
                      label={MODULE_LABELS[module]}
                      selected={localSettings[module] || ['ALL']}
                      options={moduleScopes}
                      onChange={(scopes) => handleScopeChange(module, scopes)}
                    />
                  </div>
                  <div className="text-xs text-gray-500 sm:flex-1">
                    {localSettings[module]?.includes('ALL') && 'View all records in branch'}
                    {!localSettings[module]?.includes('ALL') && localSettings[module]?.length === 1 && (
                      SCOPE_LABELS[localSettings[module][0]]?.description
                    )}
                    {!localSettings[module]?.includes('ALL') && localSettings[module]?.length === 2 && (
                      `View ${localSettings[module].map(s => s.toLowerCase()).join(' or ')} records`
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <button
              type="button"
              onClick={handleReset}
              disabled={resetMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {resetMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              Reset to Default
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Scope Options</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li><strong>All:</strong> User can view all records within their branch</li>
          <li><strong>Assigned:</strong> User can only view records assigned to them (Services & Tasks only)</li>
          <li><strong>Created:</strong> User can only view records they created</li>
          <li><strong>Assigned & Created:</strong> Select both to allow viewing assigned OR created records</li>
        </ul>
        <p className="text-xs text-blue-600 mt-2">
          Note: Invoices module does not support "Assigned" scope as invoices are not assigned to users.
        </p>
      </div>
    </div>
  );
}
